<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        @click="closeModal"
      />

      <!-- Modal panel -->
      <div
        class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
      >
        <div>
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
            <EnvelopeIcon class="h-6 w-6 text-blue-600" aria-hidden="true" />
          </div>

          <div class="mt-3 text-center sm:mt-5">
            <h3 id="modal-title" class="text-lg leading-6 font-medium text-gray-900">
              Email Verification
            </h3>

            <!-- Step 1: Enter Email -->
            <div v-if="step === 'email'" class="mt-2">
              <p class="text-sm text-gray-500 mb-4">
                Enter your email address to receive a verification code
              </p>

              <div class="mt-4">
                <label for="email" class="sr-only">Email</label>
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  name="email"
                  class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Enter your email address"
                  :disabled="loading"
                  @keyup.enter="sendVerificationCode"
                />
              </div>

              <div v-if="error" class="mt-3 text-sm text-red-600">
                {{ error }}
              </div>
            </div>

            <!-- Step 2: Enter Verification Code -->
            <div v-if="step === 'verify'" class="mt-2">
              <p class="text-sm text-gray-500 mb-4">
                We've sent a verification code to<br />
                <strong>{{ email }}</strong>
              </p>

              <div class="mt-4">
                <label for="code" class="sr-only">Verification Code</label>
                <input
                  id="code"
                  v-model="verificationCode"
                  type="text"
                  name="code"
                  class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md text-center text-lg tracking-widest"
                  placeholder="Enter 6-digit code"
                  maxlength="6"
                  :disabled="loading"
                  @keyup.enter="verifyCode"
                  @input="formatCode"
                />
              </div>

              <div v-if="error" class="mt-3 text-sm text-red-600">
                {{ error }}
              </div>

              <div
                v-if="remainingAttempts !== null && remainingAttempts < 3"
                class="mt-2 text-sm text-orange-600"
              >
                {{ remainingAttempts }} attempts remaining
              </div>

              <div v-if="expiresAt" class="mt-2 text-xs text-gray-500">
                Code expires in {{ timeRemaining }}
              </div>

              <div class="mt-4">
                <button
                  type="button"
                  class="text-sm text-blue-600 hover:text-blue-500 disabled:text-gray-400"
                  :disabled="loading || !canResend"
                  @click="resendCode"
                >
                  Didn't receive the code? Resend
                </button>
              </div>
            </div>

            <!-- Success State -->
            <div v-if="step === 'success'" class="mt-2">
              <div
                class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4"
              >
                <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
              </div>
              <p class="text-sm text-gray-500">Your email has been successfully verified!</p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
          <button
            v-if="step === 'email'"
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
            :disabled="loading || !email || !isValidEmail"
            @click="sendVerificationCode"
          >
            <span v-if="loading" class="flex items-center">
              <svg
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Sending...
            </span>
            <span v-else>Send Code</span>
          </button>

          <button
            v-if="step === 'verify'"
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
            :disabled="loading || !verificationCode || verificationCode.length !== 6"
            @click="verifyCode"
          >
            <span v-if="loading" class="flex items-center">
              <svg
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Verifying...
            </span>
            <span v-else>Verify</span>
          </button>

          <button
            v-if="step === 'success'"
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm"
            @click="closeModal"
          >
            Done
          </button>

          <button
            type="button"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
            @click="closeModal"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted, onMounted } from 'vue';
import { EnvelopeIcon, CheckIcon } from '@heroicons/vue/24/outline';
import { storageService } from '../services/storage';
import tierApi from '../services/tierApi';
import { useTierAuthStore } from '@/stores/tierAuth';

interface Props {
  isOpen: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'verified', email: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Store instances
const tierAuthStore = useTierAuthStore();

// State
const step = ref<'email' | 'verify' | 'success'>('email');
const email = ref('');
const verificationCode = ref('');
const verificationId = ref('');
const loading = ref(false);
const error = ref('');
const expiresAt = ref<Date | null>(null);
const remainingAttempts = ref<number | null>(null);
const canResend = ref(true);

// Computed
const isValidEmail = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.value);
});

const timeRemaining = computed(() => {
  if (!expiresAt.value) return '';

  const now = new Date();
  const diff = expiresAt.value.getTime() - now.getTime();

  if (diff <= 0) return 'Expired';

  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

// Timer for expiration
let expirationTimer: NodeJS.Timeout | null = null;

// Methods
const sendVerificationCode = async () => {
  if (!email.value || !isValidEmail.value) return;

  loading.value = true;
  error.value = '';

  try {
    // Ensure tierApi has the auth token
    const authToken = storageService.getAuthToken();
    if (authToken) {
      tierApi.setToken(authToken);
    }

    const response = await tierApi.verifyEmail({
      action: 'send',
      email: email.value,
    });

    if (response.success) {
      step.value = 'verify';
      verificationId.value = response.verificationId || '';
      expiresAt.value = new Date(response.expiresAt || Date.now() + 300000);
      remainingAttempts.value = null;
      startExpirationTimer();
    } else {
      error.value = response.error || 'Failed to send verification code';
    }
  } catch (err: any) {
    console.error('Send verification error:', err);
    console.error('Error response:', err?.response);
    console.error('Error data:', err?.response?.data);

    // Try to extract error message from different possible structures
    let errorMessage = 'Failed to send verification code. Please try again.';

    if (err?.response?.data?.error) {
      errorMessage = err.response.data.error;
    } else if (err?.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err?.response?.statusText) {
      errorMessage = `Request failed: ${err.response.statusText}`;
    } else if (err?.message) {
      errorMessage = err.message;
    } else if (typeof err === 'string') {
      errorMessage = err;
    }

    error.value = errorMessage;
    console.log('Final error message:', errorMessage);
  } finally {
    loading.value = false;
  }
};

const verifyCode = async () => {
  if (!verificationCode.value || verificationCode.value.length !== 6) return;

  loading.value = true;
  error.value = '';

  try {
    // Ensure tierApi has the auth token
    const authToken = storageService.getAuthToken();
    if (authToken) {
      tierApi.setToken(authToken);
    }

    const response = await tierApi.verifyEmail({
      action: 'verify',
      code: verificationCode.value,
      verificationId: verificationId.value,
    });

    if (response.success) {
      step.value = 'success';
      clearExpirationTimer();

      // Update user verification status in stores
      await tierAuthStore.updateUser({ emailVerified: true });

      emit('verified', email.value);

      // Auto close after 2 seconds
      setTimeout(() => {
        closeModal();
      }, 2000);
    } else {
      error.value = response.error || 'Invalid verification code';
      remainingAttempts.value = (response.maxAttempts || 0) - (response.attempts || 0);
    }
  } catch (err: any) {
    console.error('Verification error:', err);
    console.error('Error response:', err?.response);
    console.error('Error data:', err?.response?.data);

    // Try to extract error message from different possible structures
    let errorMessage = 'Verification failed. Please try again.';

    if (err?.response?.data?.error) {
      errorMessage = err.response.data.error;
    } else if (err?.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err?.response?.statusText) {
      errorMessage = `Request failed: ${err.response.statusText}`;
    } else if (err?.message) {
      errorMessage = err.message;
    } else if (typeof err === 'string') {
      errorMessage = err;
    }

    error.value = errorMessage;
    console.log('Final error message:', errorMessage);
  } finally {
    loading.value = false;
  }
};

const resendCode = async () => {
  canResend.value = false;
  await sendVerificationCode();

  // Prevent resend for 30 seconds
  setTimeout(() => {
    canResend.value = true;
  }, 30000);
};

const formatCode = (event: Event) => {
  const target = event.target as HTMLInputElement;
  // Remove any non-alphanumeric characters and convert to uppercase
  target.value = target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  verificationCode.value = target.value;
};

const closeModal = () => {
  // Reset state
  step.value = 'email';
  email.value = '';
  verificationCode.value = '';
  verificationId.value = '';
  error.value = '';
  expiresAt.value = null;
  remainingAttempts.value = null;
  canResend.value = true;
  clearExpirationTimer();

  emit('close');
};

const startExpirationTimer = () => {
  clearExpirationTimer();

  if (expiresAt.value) {
    const timeUntilExpiry = expiresAt.value.getTime() - Date.now();

    if (timeUntilExpiry > 0) {
      expirationTimer = setTimeout(() => {
        error.value = 'Verification code has expired. Please request a new one.';
        step.value = 'email';
      }, timeUntilExpiry);
    }
  }
};

const clearExpirationTimer = () => {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
    expirationTimer = null;
  }
};

// Watch for modal close
watch(
  () => props.isOpen,
  newValue => {
    if (!newValue) {
      clearExpirationTimer();
    }
  }
);

// Initialize auth token on mount
onMounted(() => {
  const authToken = storageService.getAuthToken();
  if (authToken) {
    tierApi.setToken(authToken);
  }
});

// Cleanup on unmount
onUnmounted(() => {
  clearExpirationTimer();
});
</script>
